import React, { useRef, useEffect } from 'react'
import SimpleBoardRow from '../components/SimpleBoardRow'
import styled from 'styled-components'

import FlipMove from 'react-flip-move'
import { useSearchParams } from 'react-router-dom'

import { useStore } from '../zustandStore'
import { shallow } from 'zustand/shallow'

const ENABLE_SCROLLING = true
// const ENABLE_SCROLLING = false
const DEFAULT_PAGE_SIZE = 16
const DEFAULT_PAGE_TIME = 2
const DEFAULT_QUERY_PARAM_NAME = 'page_time'

const ScrollingSimpleBoard = (props) => {
  const {
    players,
    totalChapterCount,
    obsState
  } = useStore(state => ({
    players: state.players,
    totalChapterCount: state.event.chapterNames.length,
    obsState: state.obsState
  }), shallow)

  // if (!players) { return null }

  const [queryParams, _] = useSearchParams()
  const highlightedPlayerSlug = queryParams.get('highlight') || props.highlightedPlayerSlug

  let pageTimeSeconds
  if (props.pageTimeQueryParam) {
    pageTimeSeconds = queryParams.get(props.pageTimeQueryParam)
  }
  if (pageTimeSeconds == null) {
    pageTimeSeconds = queryParams.get(DEFAULT_QUERY_PARAM_NAME)
  }
  if (pageTimeSeconds == null) {
    pageTimeSeconds = DEFAULT_PAGE_TIME
  }

  ////////////////////////////////////////////////////////////////////
  // BREAK UP THE PLAYERS INTO PAGES WHICH WILL BE USED FOR SCROLLING
  ////////////////////////////////////////////////////////////////////
  const pageSize = props.pageSize || DEFAULT_PAGE_SIZE
  let numberOfPages = Math.ceil(players.length / pageSize)

  let pagesOfPlayers = []
  let currentPlayerIndex = 0

  if (numberOfPages === 1) {
    // rather than figure out the subtle ways i need to refactor this to avoid
    // falling apart when there's only one page, i'm taking this easy solution
    pagesOfPlayers = [players]
  } else {
    for (let pageIndex = 0; pageIndex < numberOfPages; pageIndex++) {
      pagesOfPlayers.push([])

      // FOR THE VERTICAL BOARD:
      // if there's a page that's shorter than the others,
      // we want to consider it to be 2nd to last so the scroll points are arranged nicely
      let shortPageIndex = numberOfPages - 2
      // FOR THE HORIZONTAL BOARD:
      // we just do shit normally and make the last page the short one
      if (props.displayTwoPages) {
        shortPageIndex = numberOfPages - 1
      }

      let sizeOfThisPage = pageSize
      if (pageIndex === shortPageIndex) {
        // we want this remainder for the page size,
        // except we want a full page if that number is zero
        sizeOfThisPage = (players.length % pageSize) || pageSize
      }

      for (let playerIndex = 0; playerIndex < sizeOfThisPage; playerIndex++) {
        const currentPlayer = players[currentPlayerIndex]
        pagesOfPlayers[pageIndex].push(currentPlayer)
        currentPlayerIndex += 1
      }
    }
  }

  const minPagesForScrolling = props.displayTwoPages ? 3 : 2

  if (numberOfPages >= minPagesForScrolling) {
    // HACKY SOLUTION FOR "INFINITE" SCROLL:
    // add the first two pages onto the end, then after scrolling them onto the screen secretly jump back to start without smooth scrolling
    // to accomplish this, first we manipulate the variables as such
    if (props.displayTwoPages) {
      pagesOfPlayers.push(players.slice(0, pageSize))
      pagesOfPlayers.push(players.slice(pageSize, pageSize * 2))
      numberOfPages += 2
    } else {
      // for same "infinite scroll" trick with 1 page shown at a time
      pagesOfPlayers.push(players.slice(0, pageSize))
      numberOfPages += 1
    }
  } else {
    if (props.displayTwoPages) {
      while (pagesOfPlayers[0].length < pageSize) {
        pagesOfPlayers[0].push({})
      }
    } else {
      while (pagesOfPlayers[0].length < pageSize) {
        pagesOfPlayers[0].push({})
      }
    }
  }

  // console.log({pagesOfPlayers})

  ////////////////////////////////////////////////////////////////////
  // INITIALIZE REFS AND SET UP THE ACTUAL SCROLLING CODE
  ////////////////////////////////////////////////////////////////////
  const refs = []
  for (let i = 0; i < numberOfPages; i++) {
    // this variable number of useRef() hooks that changes with number of pages
    // causes the whole view to crap out if the number of pages changes.
    // while this is unlikely to happen for real, sometimes no-shows are purged.
    // probably need to hard reload the page if player count changes
    refs.push(useRef())
  }
  // for the horizontal board, there's no need to explicitly scroll the last page into view since we see 2 at a time
  const totalScrollPoints = (props.displayTwoPages ? numberOfPages - 1 : numberOfPages)

  // the first time this renders is the *only* time we want to call setInterval, so we do it in a useEffect
  useEffect(() => {
    // we have to use window variables instead of useState here because the setInterval won't have access to this components state variables :(
    window.scrollToRefIndex = 1 // start at index 1 because we want the first scroll to be to the 2nd element
    if (ENABLE_SCROLLING && !queryParams.get('help') && !props.disableScrolling && pagesOfPlayers.length >= minPagesForScrolling && pageTimeSeconds > 0) {
      window.scrollingIntervalId = setInterval(
        () => {
          // THIS IS WHERE THE INFINITE SCROLL ACTUALLY HAPPENS
          // if it's time to wrap back around to start, we're actually at the duplicated
          // first page on the end. so we snap back to start without smooth scrolling so it's hidden, then
          // advance the index by 1 and we never stopped scrolling right
          if (window.scrollToRefIndex === 0) {
            refs[0].current.scrollIntoView({ inline: 'start' })
            window.scrollToRefIndex = 1
          }
          refs[window.scrollToRefIndex].current.scrollIntoView({ behavior: 'smooth', inline: 'start' })
          window.scrollToRefIndex = (window.scrollToRefIndex + 1) % totalScrollPoints
        },
        pageTimeSeconds * 1000
      )
    }

    // dev (styling) aid to be used in conjunction with commenting out the entire useEffect above this point
    // refs[2].current.scrollIntoView({ behavior: 'smooth', inline: 'start' })

    // clear interval on unmount
    return (() => {
      clearInterval(window.scrollingIntervalId)
      window.scrollingIntervalId = null
    })
  }, [])

  const onClick = props.onClick ? { onClick: props.onClick } : {}

  const showLiveIcons = obsState && obsState.livePlayerSlugs

  return (
    <WrapperDiv {...onClick} className='simple-board-wrapper'>
      <div className='simple-board-table'>
        {/*
          all this crap is how we make sure each page is indented the right amount, by checking for the longest rank on the page (0 1 2 or 3 digits)
          however, if the number of pages doesn't divide evenly into the number of players, one page will be short. it's actually easiest if this
          is the 2nd to last page so we can just scroll the first player on each page to the top. however, that means the last few players can be
          visible on more than one page, so to avoid weirdness when there's a weird short page, in that case the 2nd to last page has to be indented
          the same amount as the last page. it's possible for either of the last two pages to be indented more than the other, so 
          IF THERE'S A SHORT PAGE, WE NEED TO CHECK BOTH THE 2ND TO LAST AND LAST AND USE THE LARGER NUMBER
        */}
        {pagesOfPlayers.map((thisPageOfPlayers, pageIndex) => {
          let pageToGetMaxRankLength = thisPageOfPlayers

          if (!props.displayTwoPages) {
            // subtract 2 & 3 because the first page is duplicated at the end for sneaky fake infinite downward scroll
            // i.e. subtracting 1 gives actual last element, 1 extra to account for duplicate first page, and 1 more to get to 2nd to last
            const isOneOfLastTwoPages = [numberOfPages - 3, numberOfPages - 2].includes(pageIndex)
            if (isOneOfLastTwoPages && pagesOfPlayers.length > 1) {
              // we need to know this for both the 2nd to last and last pages, but 2nd to last is the only one that can be the short page
              const hasShortPage = pagesOfPlayers[numberOfPages - 3].length < pageSize
              if (hasShortPage) {
                //in the case of a short page, the "page" we check for max rank digits is the union of them both
                pageToGetMaxRankLength = [...pagesOfPlayers[numberOfPages - 3], ...pagesOfPlayers[numberOfPages - 2]]
              }
            }
          }

          {/* from this point forward the code no longer worries about weird short page/last page scenario described above and just does its thing */ }
          const ranksInPage = pageToGetMaxRankLength.map(p => p.completedAllChapters ? p.rank : '')
          const maxRankDigitsInPage = Math.max(...ranksInPage.map(r => String(r).length))

          return (
            <div className={`page page-${pageIndex + 1} ${thisPageOfPlayers.length}-rows`} key={pageIndex} ref={refs[pageIndex]}>
              <FlipMove className='page-flex' enterAnimation="elevator" leaveAnimation="fade">
                {thisPageOfPlayers.map((player, i) =>
                  <SimpleBoardRow
                    player={player}
                    className='row'
                    isPlaceholder={!player.slug}
                    key={player.slug || i}
                    isHighlighted={player.slug === highlightedPlayerSlug}
                    rankDigits={maxRankDigitsInPage}
                    totalChapterCount={totalChapterCount}
                    isLive={queryParams.get('help')
                      ? i === 0 || i === pageSize - 1 || i % 3 === 0
                      : showLiveIcons && obsState.livePlayerSlugs.includes(player.slug)
                    }
                    isFeatured={obsState && obsState.featuredPlayerSlug == player.slug}
                  />
                )}
              </FlipMove>
            </div>
          )
        })}
      </div>
    </WrapperDiv>
  )
}

export default ScrollingSimpleBoard

const WrapperDiv = styled.div`
  width: 100%;

`;
